import { FC, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { Course, ApplicationFormDataV2 } from "../types";
import { formatPrice } from "../utils/validation";
import PhoneInput from "./PhoneInput";

// International phone validation - accepts various formats
const phoneSchema = z
  .string()
  .min(6, "Phone number too short")
  .max(15, "Phone number too long")
  .regex(/^\d+$/, "Enter digits only")
  .optional()
  .or(z.literal(""));

// Validation schema - all fields optional except courseId
const formSchema = z.object({
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  // Phone with country codes
  mobileCountryCode: z.string().optional(),
  mobile: phoneSchema,
  altMobileCountryCode: z.string().optional(),
  altMobile: phoneSchema,
  emergencyCountryCode: z.string().optional(),
  emergencyContact: phoneSchema,
  // Email
  email: z.string().email("Enter valid email").optional().or(z.literal("")),
  altEmail: z.string().email("Enter valid email").optional().or(z.literal("")),
  // Professional
  profession: z.enum(["STUDENT", "WORKING_PROFESSIONAL", "OTHER"]).optional(),
  organizationName: z.string().max(200).optional(),
  institutionName: z.string().max(200).optional(),
  // Course
  courseId: z.string().min(1, "Please select a course"),
  batch: z.string().max(100).optional(),
  motivation: z.string().max(1000).optional(),
  referralSource: z.string().max(200).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface ApplicationFormV2Props {
  courses: Course[];
  onSubmit: (data: ApplicationFormDataV2) => Promise<void>;
  isSubmitting: boolean;
}

/**
 * V2 Application Form with grouped sections
 * International phone number support
 * Follows Indian fintech UX best practices
 */
const ApplicationFormV2: FC<ApplicationFormV2Props> = ({
  courses,
  onSubmit,
  isSubmitting,
}) => {
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      courseId: "",
      mobileCountryCode: "+91",
      altMobileCountryCode: "+91",
      emergencyCountryCode: "+91",
    },
  });

  const selectedCourseId = watch("courseId");
  const selectedCourse = courses.find((c) => c.id === selectedCourseId);

  // Handle photo selection
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Form submit handler
  const onFormSubmit = async (data: FormData) => {
    await onSubmit({
      ...data,
      photo: photoFile || undefined,
    });
  };

  const referralOptions = [
    "Google Search",
    "Social Media (Instagram/Facebook)",
    "LinkedIn",
    "YouTube",
    "Friend/Family Referral",
    "Previous Student",
    "College/University",
    "Newspaper/Magazine",
    "Other",
  ];

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-8">
      {/* Course Selection */}
      <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-primary text-white text-sm">
            1
          </span>
          Select Course
        </h3>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <label
              key={course.id}
              className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-md ${
                selectedCourseId === course.id
                  ? "border-brand-secondary bg-teal-50 ring-2 ring-brand-secondary/20"
                  : "border-neutral-200 hover:border-neutral-300"
              }`}
            >
              <input
                type="radio"
                {...register("courseId")}
                value={course.id}
                className="sr-only"
              />
              {selectedCourseId === course.id && (
                <div className="absolute right-3 top-3">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-secondary text-white text-xs">
                    ✓
                  </div>
                </div>
              )}
              <span className="inline-flex items-center rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800">
                {course.type}
              </span>
              <h4 className="mt-2 font-medium text-neutral-900">{course.name}</h4>
              <p className="mt-1 text-sm text-neutral-500 line-clamp-2">
                {course.description}
              </p>
              <p className="mt-2 text-lg font-bold text-brand-secondary">
                {formatPrice(course.price)}
              </p>
            </label>
          ))}
        </div>
        {errors.courseId && (
          <p className="mt-2 text-sm text-red-600">{errors.courseId.message}</p>
        )}
      </section>

      {/* Personal Information */}
      <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-primary text-white text-sm">
            2
          </span>
          Personal Information
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              First Name
            </label>
            <input
              {...register("firstName")}
              type="text"
              placeholder="Enter first name"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Last Name
            </label>
            <input
              {...register("lastName")}
              type="text"
              placeholder="Enter last name"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
            />
          </div>

          {/* Mobile Number with International Support */}
          <Controller
            name="mobile"
            control={control}
            render={({ field }) => (
              <PhoneInput
                label="Mobile Number"
                countryCodeValue={watch("mobileCountryCode") || "+91"}
                phoneValue={field.value || ""}
                onCountryCodeChange={(code) => setValue("mobileCountryCode", code)}
                onPhoneChange={field.onChange}
                placeholder="Enter phone number"
                error={errors.mobile?.message}
              />
            )}
          />

          {/* Alternate Mobile */}
          <Controller
            name="altMobile"
            control={control}
            render={({ field }) => (
              <PhoneInput
                label="Alternate Mobile"
                countryCodeValue={watch("altMobileCountryCode") || "+91"}
                phoneValue={field.value || ""}
                onCountryCodeChange={(code) => setValue("altMobileCountryCode", code)}
                onPhoneChange={field.onChange}
                placeholder="Optional"
              />
            )}
          />

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Email Address
            </label>
            <input
              {...register("email")}
              type="email"
              placeholder="you@example.com"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Alternate Email */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Alternate Email
            </label>
            <input
              {...register("altEmail")}
              type="email"
              placeholder="Optional"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
            />
          </div>

          {/* Emergency Contact */}
          <Controller
            name="emergencyContact"
            control={control}
            render={({ field }) => (
              <PhoneInput
                label="Emergency Contact"
                countryCodeValue={watch("emergencyCountryCode") || "+91"}
                phoneValue={field.value || ""}
                onCountryCodeChange={(code) => setValue("emergencyCountryCode", code)}
                onPhoneChange={field.onChange}
                placeholder="Optional"
              />
            )}
          />

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Photo (Optional)
            </label>
            <div className="flex items-center gap-4">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-16 h-16 rounded-full object-cover border-2 border-neutral-200"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <label className="cursor-pointer px-4 py-2 rounded-lg border border-neutral-300 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors">
                Choose File
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="sr-only"
                />
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* Professional / Academic */}
      <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-primary text-white text-sm">
            3
          </span>
          Professional / Academic
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Current Profession
            </label>
            <select
              {...register("profession")}
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all bg-white"
            >
              <option value="">Select...</option>
              <option value="STUDENT">Student</option>
              <option value="WORKING_PROFESSIONAL">Working Professional</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Organization / Company
            </label>
            <input
              {...register("organizationName")}
              type="text"
              placeholder="Where do you work?"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Institution / College / School
            </label>
            <input
              {...register("institutionName")}
              type="text"
              placeholder="Where do you study?"
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all"
            />
          </div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-primary text-white text-sm">
            4
          </span>
          Additional Information
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Why do you want to join this program?
            </label>
            <textarea
              {...register("motivation")}
              rows={3}
              placeholder="Tell us about your motivation..."
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              How did you hear about us?
            </label>
            <select
              {...register("referralSource")}
              className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none transition-all bg-white"
            >
              <option value="">Select...</option>
              {referralOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Submit Section */}
      <section className="bg-teal-50 rounded-xl border border-teal-200 p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            {selectedCourse && (
              <div className="text-center sm:text-left">
                <p className="text-sm text-neutral-600">Selected Course</p>
                <p className="font-semibold text-neutral-900">{selectedCourse.name}</p>
                <p className="text-2xl font-bold text-brand-secondary">
                  {formatPrice(selectedCourse.price)}
                </p>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || !selectedCourseId}
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-brand-secondary text-white font-semibold shadow-lg hover:bg-teal-800 focus:ring-4 focus:ring-brand-secondary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Processing...
              </span>
            ) : (
              "Continue to Payment →"
            )}
          </button>
        </div>
      </section>
    </form>
  );
};

export default ApplicationFormV2;
